import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, Ban, Eye, Printer, Calendar as CalendarIcon, ScanLine, Store } from "lucide-react";
import { toast } from "sonner";
import { printTicket } from "../components/PrintableTicket";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import QRScannerModal from "../components/QRScannerModal";

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const daysAgo = (n) => {
  const d = today();
  d.setDate(d.getDate() - n);
  return d;
};

export default function Tickets() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ from: daysAgo(6), to: today() });
  const [openCalendar, setOpenCalendar] = useState(false);

  // Cancel-with-confirmation flow
  const [cancelTarget, setCancelTarget] = useState(null); // ticket to cancel
  const [confirmValue, setConfirmValue] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (range?.from) params.date_from = ymd(range.from);
      if (range?.to) params.date_to = ymd(range.to);
      params.limit = 200;
      const { data } = await api.get("/tickets", { params });
      setTickets(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, range]);

  useEffect(() => { load(); }, [load]);

  const openCancel = (t) => {
    setCancelTarget(t);
    setConfirmValue("");
  };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    const value = (confirmValue || "").trim().toUpperCase();
    if (!value) {
      toast.error("Escanea el QR o digita el número del ticket");
      return;
    }
    if (value !== (cancelTarget.ticket_number || "").toUpperCase()) {
      toast.error("El número no coincide con el ticket");
      return;
    }
    setCancelling(true);
    try {
      await api.post(`/tickets/${cancelTarget.id}/cancel`, { confirm_ticket_number: value });
      toast.success(`Ticket ${cancelTarget.ticket_number} anulado`);
      setCancelTarget(null);
      setConfirmValue("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const onQrDecoded = (text) => {
    setScannerOpen(false);
    // QR content may be a URL like /t/BR01-... — extract the last path segment
    let decoded = (text || "").trim();
    try {
      const url = new URL(decoded);
      const parts = url.pathname.split("/").filter(Boolean);
      if (parts.length) decoded = parts[parts.length - 1];
    } catch {}
    setConfirmValue(decoded.toUpperCase());
  };

  const statusBadge = (s) => {
    const map = {
      active: "bg-blue-100 text-blue-700",
      cancelled: "bg-red-100 text-red-700",
      paid: "bg-green-100 text-green-700",
    };
    const label = { active: "Activo", cancelled: "Anulado", paid: "Pagado" };
    return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${map[s] || "bg-slate-100 text-slate-600"}`}>{label[s] || s}</span>;
  };

  const canCancel = (t) => {
    if (t.status !== "active") return false;
    if (hasRole("super_admin", "admin", "supervisor")) return true;
    return hasRole("cashier");
  };

  const setQuickRange = (fn) => {
    setRange(fn());
    setOpenCalendar(false);
  };

  const rangeLabel = () => {
    if (!range?.from) return "Rango de fechas";
    const f = range.from.toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric" });
    if (!range.to || ymd(range.from) === ymd(range.to)) return f;
    const t = range.to.toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric" });
    return `${f} → ${t}`;
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Tickets"
        subtitle="Búsqueda, consulta y anulación de boletos"
        testId="tickets-header"
        actions={hasRole("cashier") ? (
          <Button
            onClick={() => navigate("/pos")}
            className="bg-blue-600 hover:bg-blue-700 rounded-sm h-10"
            data-testid="tickets-back-to-pos"
          >
            <Store className="w-4 h-4 mr-2" />
            Volver al POS
          </Button>
        ) : null}
      />

      <div className="p-6 sm:p-8 space-y-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Buscar por número de ticket…"
              className="pl-9 rounded-sm border-slate-300"
              data-testid="ticket-search"
            />
          </div>

          <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="rounded-sm border-slate-300 justify-start font-normal"
                data-testid="tickets-date-range"
              >
                <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                {rangeLabel()}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-sm" align="start">
              <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200">
                <Button size="sm" variant="ghost" className="rounded-sm text-xs" onClick={() => setQuickRange(() => ({ from: today(), to: today() }))} data-testid="range-today">Hoy</Button>
                <Button size="sm" variant="ghost" className="rounded-sm text-xs" onClick={() => setQuickRange(() => ({ from: daysAgo(6), to: today() }))} data-testid="range-7d">Últimos 7 días</Button>
                <Button size="sm" variant="ghost" className="rounded-sm text-xs" onClick={() => setQuickRange(() => ({ from: daysAgo(29), to: today() }))} data-testid="range-30d">Últimos 30 días</Button>
              </div>
              <Calendar
                mode="range"
                selected={range}
                onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                numberOfMonths={1}
                defaultMonth={range?.from || new Date()}
              />
            </PopoverContent>
          </Popover>

          <Button onClick={load} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="ticket-search-btn">
            Buscar
          </Button>
        </div>

        {/* Desktop: table view (>= md) */}
        <div className="hidden md:block bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Ticket</Th><Th>Fecha</Th><Th>Cajero</Th><Th>Sucursal</Th><Th>Jugadas</Th><Th>Total</Th><Th>Estado</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="p-6 text-center text-slate-500 text-sm">Cargando…</td></tr>}
              {!loading && tickets.length === 0 && (
                <tr><td colSpan="8" className="p-8 text-center text-slate-500 text-sm">Sin tickets en el rango seleccionado</td></tr>
              )}
              {tickets.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ticket-row-${t.ticket_number}`}>
                  <td className="p-3 font-mono font-bold text-slate-950">{t.ticket_number}</td>
                  <td className="p-3 text-slate-700 font-mono text-xs">{new Date(t.created_at).toLocaleString("es-DO")}</td>
                  <td className="p-3 text-slate-700">{t.user_name}</td>
                  <td className="p-3 font-mono text-slate-700">{t.branch_code}</td>
                  <td className="p-3 font-mono text-slate-700">{t.items.length}</td>
                  <td className="p-3 font-mono font-bold text-slate-950 text-right">RD${t.total_amount.toFixed(2)}</td>
                  <td className="p-3">{statusBadge(t.status)}</td>
                  <td className="p-3 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="outline" className="h-8 rounded-sm border-slate-300" onClick={() => setSelected(t)} data-testid={`ticket-view-${t.ticket_number}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 rounded-sm border-slate-300" onClick={() => printTicket(t, { isReprint: true })} data-testid={`ticket-print-${t.ticket_number}`}>
                      <Printer className="w-3 h-3" />
                    </Button>
                    {canCancel(t) && (
                      <Button size="sm" variant="outline" className="h-8 rounded-sm border-red-300 text-red-700 hover:bg-red-50" onClick={() => openCancel(t)} data-testid={`ticket-cancel-${t.ticket_number}`}>
                        <Ban className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: card view (< md) */}
        <div className="md:hidden space-y-3">
          {loading && <div className="p-6 text-center text-slate-500 text-sm bg-white border border-slate-200 rounded-sm">Cargando…</div>}
          {!loading && tickets.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm bg-white border border-slate-200 rounded-sm">Sin tickets en el rango seleccionado</div>
          )}
          {tickets.map((t) => (
            <div
              key={t.id}
              className="bg-white border border-slate-200 rounded-sm overflow-hidden"
              data-testid={`ticket-card-${t.ticket_number}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono font-black text-slate-950 text-sm break-all leading-tight">
                      {t.ticket_number}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">
                      {new Date(t.created_at).toLocaleString("es-DO")}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-black text-slate-950">RD${t.total_amount.toFixed(2)}</div>
                    <div className="mt-1">{statusBadge(t.status)}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-600">
                  <span><b>Cajero:</b> {t.user_name}</span>
                  <span><b>Sucursal:</b> <span className="font-mono">{t.branch_code}</span></span>
                  <span><b>Jugadas:</b> <span className="font-mono">{t.items.length}</span></span>
                </div>
              </div>
              <div className="grid grid-cols-3 border-t border-slate-200 divide-x divide-slate-200">
                <button
                  onClick={() => setSelected(t)}
                  className="py-3 flex items-center justify-center gap-1.5 text-xs font-bold text-slate-700 active:bg-slate-100 uppercase tracking-wider"
                  data-testid={`ticket-view-${t.ticket_number}`}
                >
                  <Eye className="w-4 h-4" /> Ver
                </button>
                <button
                  onClick={() => printTicket(t, { isReprint: true })}
                  className="py-3 flex items-center justify-center gap-1.5 text-xs font-bold text-slate-700 active:bg-slate-100 uppercase tracking-wider"
                  data-testid={`ticket-print-${t.ticket_number}`}
                >
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                {canCancel(t) ? (
                  <button
                    onClick={() => openCancel(t)}
                    className="py-3 flex items-center justify-center gap-1.5 text-xs font-bold text-red-700 active:bg-red-50 uppercase tracking-wider"
                    data-testid={`ticket-cancel-${t.ticket_number}`}
                  >
                    <Ban className="w-4 h-4" /> Anular
                  </button>
                ) : (
                  <div className="py-3 flex items-center justify-center text-xs text-slate-400 uppercase tracking-wider">
                    —
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ticket details */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-mono font-black">{selected?.ticket_number}</DialogTitle>
            <DialogDescription>
              {selected && new Date(selected.created_at).toLocaleString("es-DO")}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="font-mono text-sm space-y-1">
              <div className="text-xs text-slate-500">{selected.branch_code} · {selected.user_name}</div>
              <div className="border-t border-dashed border-slate-300 my-2" />
              {selected.items.map((it) => (
                <div key={it.id} className="py-1">
                  <div className="text-xs text-slate-500">{it.game_name} · {it.play_type_name}</div>
                  <div className="flex justify-between">
                    <span className="font-bold">{it.display}</span>
                    <span>RD${it.amount.toFixed(2)}</span>
                  </div>
                  <div className="text-[10px] text-green-700">Premio potencial: RD${it.potential_prize.toFixed(2)}</div>
                </div>
              ))}
              <div className="border-t border-slate-300 mt-3 pt-2 flex justify-between font-black">
                <span>TOTAL</span><span>RD${selected.total_amount.toFixed(2)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirm modal */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setConfirmValue(""); } }}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-red-700 font-black tracking-tight">Anular ticket</DialogTitle>
            <DialogDescription>
              Escanea el código QR del ticket o digita su número para confirmar la anulación. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm font-mono text-xs">
                <div className="text-slate-500">Ticket seleccionado:</div>
                <div className="font-bold text-slate-950 text-base tracking-wider">{cancelTarget.ticket_number}</div>
                <div className="text-slate-600 mt-1">RD${cancelTarget.total_amount.toFixed(2)} · {cancelTarget.items.length} jugada(s)</div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={confirmValue}
                  onChange={(e) => setConfirmValue(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && submitCancel()}
                  placeholder="Ej. BR01-20260910-000123"
                  className="rounded-sm border-slate-300 font-mono uppercase tracking-wider"
                  autoFocus
                  data-testid="cancel-confirm-input"
                />
                <Button
                  variant="outline"
                  className="rounded-sm border-slate-300"
                  onClick={() => setScannerOpen(true)}
                  data-testid="cancel-scan-qr"
                  title="Escanear QR"
                >
                  <ScanLine className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm border-slate-300"
              onClick={() => { setCancelTarget(null); setConfirmValue(""); }}
              disabled={cancelling}
              data-testid="cancel-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 rounded-sm text-white"
              onClick={submitCancel}
              disabled={cancelling}
              data-testid="cancel-confirm-btn"
            >
              {cancelling ? "Anulando…" : "Anular ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QRScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onDecode={onQrDecoded} />
    </div>
  );
}

const Th = ({ children }) => (
  <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">{children}</th>
);
